import { PORTABLE_BLOB_COUNT_MAX } from './constants.js';
import { assertBlobIntegrity, decodeBase64, encodeBase64 } from './bytes.js';
import { StorageError, mapStorageError } from './errors.js';
import { MemoryStorageBackend } from './memory.js';
import { defaultClock } from './model.js';
import { assertPortableDataset } from './validation.js';
function browserStorageManager() {
    if (typeof navigator === 'undefined' || !navigator.storage)
        return undefined;
    return navigator.storage;
}
async function validateImport(datasetInput) {
    assertPortableDataset(datasetInput);
    const dataset = structuredClone(datasetInput);
    const blobs = [];
    for (const portableBlob of dataset.blobs) {
        const bytes = decodeBase64(portableBlob.dataBase64);
        const buffer = new ArrayBuffer(bytes.byteLength);
        new Uint8Array(buffer).set(bytes);
        const blob = new Blob([buffer], { type: portableBlob.ref.mime });
        await assertBlobIntegrity(blob, portableBlob.ref);
        blobs.push({ ref: portableBlob.ref, blob });
    }
    return { dataset, blobs };
}
async function exportBlobs(blobStore) {
    const refs = await blobStore.list({ limit: PORTABLE_BLOB_COUNT_MAX });
    const blobs = [];
    for (const ref of refs) {
        const blob = await blobStore.get(ref);
        if (!blob)
            throw new StorageError('integrity-failed', { detail: `Blob ${ref.id} is missing` });
        await assertBlobIntegrity(blob, ref);
        blobs.push({
            ref: structuredClone(ref),
            dataBase64: encodeBase64(new Uint8Array(await blob.arrayBuffer())),
        });
    }
    return blobs;
}
async function storageDiagnostics(parts, requestPersistence) {
    const manager = parts.storageManager;
    const persistenceSupported = typeof manager?.persist === 'function'
        || typeof manager?.persisted === 'function';
    let persistenceRequested = false;
    let persisted = null;
    try {
        if (requestPersistence && manager?.persist) {
            persistenceRequested = true;
            persisted = await manager.persist();
        }
        else if (manager?.persisted) {
            persisted = await manager.persisted();
        }
    }
    catch {
        persisted = null;
    }
    let usageBytes = null;
    let quotaBytes = null;
    try {
        const estimate = await manager?.estimate?.();
        usageBytes = estimate?.usage ?? await parts.admin.estimateUsageBytes();
        quotaBytes = estimate?.quota ?? null;
    }
    catch {
        usageBytes = await parts.admin.estimateUsageBytes();
    }
    const usageRatio = usageBytes !== null && quotaBytes !== null && quotaBytes > 0
        ? usageBytes / quotaBytes
        : null;
    return {
        mode: parts.mode,
        blobBackend: parts.blobs.backendKind,
        persistenceSupported,
        persisted,
        persistenceRequested,
        usageBytes,
        quotaBytes,
        usageRatio,
    };
}
export function createStorageProvider(parts) {
    let closed = false;
    const assertOpen = () => {
        if (closed)
            throw new StorageError('closed');
    };
    return {
        mode: parts.mode,
        documents: parts.documents,
        outbox: parts.outbox,
        blobs: parts.blobs,
        getSchemaVersion: () => {
            assertOpen();
            return parts.admin.getSchemaVersion();
        },
        migrate: (migrations) => {
            assertOpen();
            return parts.admin.migrate(migrations);
        },
        exportDataset: async () => {
            assertOpen();
            const logical = await parts.admin.snapshot();
            const dataset = {
                format: 'tchoma-storage',
                formatVersion: 1,
                schemaVersion: logical.schemaVersion,
                exportedAt: parts.clock.now(),
                mode: parts.mode,
                documents: logical.documents,
                outbox: logical.outbox,
                blobs: await exportBlobs(parts.blobs),
            };
            assertPortableDataset(dataset);
            return dataset;
        },
        importDataset: async (datasetInput, options = {}) => {
            assertOpen();
            const validated = await validateImport(datasetInput);
            const logical = {
                schemaVersion: validated.dataset.schemaVersion,
                documents: validated.dataset.documents,
                outbox: parts.mode === 'local-only' ? [] : validated.dataset.outbox,
            };
            const previousRefs = options.mode === 'merge'
                ? []
                : await parts.blobs.list({ limit: PORTABLE_BLOB_COUNT_MAX });
            try {
                for (const item of validated.blobs)
                    await parts.blobs.putPrepared(item.ref, item.blob);
                if (options.mode === 'merge')
                    await parts.admin.merge(logical);
                else
                    await parts.admin.replace(logical);
            }
            catch (error) {
                throw mapStorageError(error);
            }
            if (options.mode !== 'merge') {
                const importedIds = new Set(validated.blobs.map(({ ref }) => ref.id));
                for (const ref of previousRefs) {
                    if (!importedIds.has(ref.id))
                        await parts.blobs.delete(ref);
                }
            }
            return {
                documents: logical.documents.length,
                outboxOperations: logical.outbox.length,
                blobs: validated.blobs.length,
            };
        },
        diagnostics: (options = {}) => {
            assertOpen();
            return storageDiagnostics(parts, options.requestPersistence === true);
        },
        close: () => {
            if (closed)
                return;
            closed = true;
            parts.admin.close();
        },
    };
}
export function createMemoryStorage(options = {}) {
    const clock = options.clock ?? defaultClock();
    const backend = new MemoryStorageBackend({ ...options, clock });
    return createStorageProvider({
        mode: backend.mode,
        documents: backend.documents,
        outbox: backend.outbox,
        blobs: backend.blobs,
        admin: backend,
        clock,
        storageManager: options.storageManager ?? browserStorageManager(),
    });
}
//# sourceMappingURL=provider.js.map